import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './PhoneChangeModal.module.scss'
import { certificateNice } from '../../utils/certificateNice'
import { useFormContext } from 'react-hook-form'
import { useIsMobile } from '../../hooks'

export const PhoneChangeModal = ({ isOpen, onClose, name, phoneNumber, onSubmit }) => {
	const popupRef = useRef(null)
	const [isChanged, setIsChanged] = useState(false)
	const [newName, setNewName] = useState(name)
	const [newPhone, setNewPhone] = useState(phoneNumber)
	const [error, setError] = useState('')
	const [info, setInfo] = useState({ name: '', phoneNumber: '' })
	const [message, setMessage] = useState('')
	const [verifiedStatus, setVerifiedStatus] = useState('notTried')
	const { getValues, setValue } = useFormContext()
	const isMobile = useIsMobile()

	const infoList = [
		{ label: '이름', value: info.name },
		{ label: '휴대폰번호', value: info.phoneNumber },
	]
	const handleVerificationSuccess = useCallback(
		(info) => {
			setValue('name', info.name)
			setValue('phoneNumber', info.phoneNumber)
			setIsChanged(true)
		},
		[setValue],
	)

	const NICE_CERT_STORAGE_KEY = 'niceCertificationResult'
	const NICE_CERT_START_KEY = 'niceCertificationStart'

	// 인증 결과 처리 함수
	const processCertificationResult = useCallback(
		(result) => {
			const { status, message: resultMessage, userInfo } = result

			if (status === 'success') {
				console.log('Nice 본인인증 최종 성공! 받은 데이터:', userInfo)
				const newInfo = {
					name: userInfo.name,
					phoneNumber: userInfo.phoneNumber, // Ensure this matches your backend's field name (phone vs phoneNumber)
				}
				setInfo(newInfo) // Update local state for display
				setVerifiedStatus('success') // Update status in parent for UI logic
				setMessage('본인인증에 성공했습니다.') // Display success message
				handleVerificationSuccess(newInfo) // Callback to parent to fill form fields
			} else {
				console.error('Nice 본인인증 최종 실패:', resultMessage)
				setInfo({ name: '', phoneNumber: '' }) // Clear info on failure
				setVerifiedStatus('failed') // Update status in parent
				setMessage(`본인인증에 실패했어요: ${resultMessage}`) // Display failure message
			}
		},
		[setVerifiedStatus, handleVerificationSuccess],
	)

	const handleMessageFromPopup = useCallback(
		(event) => {
			// 팝업인 경우에만 source 체크
			if (popupRef.current && event.source !== popupRef.current) return
			// IMPORTANT: Validate the origin for security!
			// This should match the exact domain of your parent page (where SelfVerification is rendered).
			// For local development, it might be 'http://localhost:3000'.
			// For production, it must be 'https://your-actual-domain.com'.
			// The popup (CertifyComplete.jsx) sends with window.location.origin.
			if (event.origin !== window.location.origin) {
				console.warn('Message received from unknown origin:', event.origin)
				return // Ignore messages from untrusted origins
			}

			// Check if it's the specific message type from your Nice certification complete page
			if (event.data && event.data.type === 'niceCertificationFinalResult') {
				processCertificationResult(event.data)
			}
		},
		[processCertificationResult],
	) // Dependencies for useCallback

	// sessionStorage 및 localStorage에서 인증 결과 확인 (새 창으로 이동한 경우)
	const checkStorageForResult = useCallback(() => {
		try {
			// sessionStorage 먼저 확인
			let storedResult = sessionStorage.getItem(NICE_CERT_STORAGE_KEY)
			if (!storedResult) {
				// sessionStorage에 없으면 localStorage 확인
				storedResult = localStorage.getItem(NICE_CERT_STORAGE_KEY)
			}

			if (storedResult) {
				const result = JSON.parse(storedResult)
				console.log('storage에서 인증 결과 발견:', result)
				processCertificationResult(result)
				// 처리 후 storage에서 제거
				sessionStorage.removeItem(NICE_CERT_STORAGE_KEY)
				localStorage.removeItem(NICE_CERT_STORAGE_KEY)
				localStorage.removeItem(NICE_CERT_START_KEY) // 인증 시작 정보도 제거
			}
		} catch (error) {
			console.error('storage에서 인증 결과 읽기 실패:', error)
		}
	}, [processCertificationResult])

	const handleCancel = () => {
		setIsChanged(false)
		setNewName(name)
		setNewPhone(phoneNumber)
		setError('')
		onClose()
	}

	const handleComplete = () => {
		if (!isChanged) return
		if (newName !== name) {
			setError('기존 회원정보와 일치하지 않아요.')
			return
		}
		onSubmit({ name: getValues('name'), phoneNumber: getValues('phoneNumber') })
		console.log('name 키에는 이게 담겨있고 ', getValues('name'))
		console.log('phoneNumber 키에는 이게 담겨있다 ', getValues('phoneNumber'))

		onClose()
	}

	const handleVerificationNice = async () => {
		try {
			const currentPath = window.location.pathname
			const currentUrl = window.location.origin + currentPath // 전체 URL (origin + pathname)

			// 환경에 따라 returnURL 설정
			// - 웹: 클라이언트 /certify/complete (GET 파라미터로 처리)
			// - 모바일: 백엔드 서버 /certify/complete (POST로 처리 후 리다이렉트)
			let returnURL
			if (isMobile) {
				// 모바일: 백엔드 서버로 설정 (redirect 쿼리 파라미터에 전체 URL 포함)
				const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || window.location.origin
				returnURL = `${apiEndpoint}/users/certify/complete?redirect=${encodeURIComponent(currentUrl)}`
			} else {
				// 웹: 클라이언트로 설정
				returnURL = `${window.location.origin}/certify/complete`
			}

			console.log('시도중', { currentPath, currentUrl, returnURL, isMobile })

			// 인증 시작 정보를 localStorage에 저장 (모바일 앱 전환 대비)
			localStorage.setItem(
				NICE_CERT_START_KEY,
				JSON.stringify({
					timestamp: Date.now(),
					returnURL: returnURL,
					originalPath: currentPath, // 원래 페이지 경로 저장
				}),
			)

			popupRef.current = await certificateNice(returnURL, 'popupForPhone')
			console.log('certificateNice가 잘 실행되었다')
			// const result = await randomCheckVerification()

			// if (result.success) {
			// 	const newInfo = { name: result.name, phoneNumber: result.phoneNumber }
			// 	setInfo(newInfo)
			// 	changeVerifiedStatus('success')
			// 	setMessage('본인인증에 성공했습니다')
			// 	onVerificationSuccess(newInfo)
			// 	console.log('성공')
			// } else {
			// 	setInfo({ classNameame: '', phoneNumber: '' })
			// 	changeVerifiedStatus('failed')
			// 	setMessage('본인인증에 실패했어요')
			// }
		} catch (error) {
			setInfo({ name: '', phoneNumber: '' })
			setVerifiedStatus('failed')
			setMessage('인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
			console.error('인증 오류:', error)
		}
	}

	// Effect hook to add and remove the message event listener
	useEffect(() => {
		window.addEventListener('message', handleMessageFromPopup)

		// Cleanup function: remove the event listener when the component unmounts
		return () => {
			window.removeEventListener('message', handleMessageFromPopup)
		}
	}, [handleMessageFromPopup]) // Re-run effect if handleMessageFromPopup changes

	// 컴포넌트 마운트 시 및 페이지 포커스 시 storage 확인
	useEffect(() => {
		if (!isOpen) return // 모달이 열려있을 때만 확인

		// 초기 마운트 시 확인
		checkStorageForResult()

		// 페이지 포커스 시 확인 (새 창에서 돌아왔을 때)
		const handleFocus = () => {
			checkStorageForResult()
		}

		// visibilitychange 이벤트로도 확인 (탭 전환 시)
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				// 약간의 지연을 두어 Pass 앱에서 돌아온 후 처리되도록
				setTimeout(() => {
					checkStorageForResult()
				}, 300)
			}
		}

		// 주기적으로 확인 (모바일에서 앱 전환 후 돌아올 때를 대비)
		const checkInterval = setInterval(() => {
			// 인증 시작 정보가 있으면 결과를 확인
			const certStart = localStorage.getItem(NICE_CERT_START_KEY)
			if (certStart) {
				const startTime = JSON.parse(certStart).timestamp
				const now = Date.now()
				// 5분 이내의 인증 시도만 확인 (타임아웃 방지)
				if (now - startTime < 5 * 60 * 1000) {
					checkStorageForResult()
				} else {
					// 타임아웃된 경우 정리
					localStorage.removeItem(NICE_CERT_START_KEY)
				}
			}
		}, 2000) // 2초마다 확인

		window.addEventListener('focus', handleFocus)
		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			window.removeEventListener('focus', handleFocus)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			clearInterval(checkInterval)
		}
	}, [checkStorageForResult, isOpen])

	if (!isOpen) return null

	return (
		<div className={styles.overlay}>
			<div className={styles.modal}>
				<h2 className={styles.title}>연락처 변경</h2>
				<div className={styles.section}>
					<div className={styles.sectionTitle}>[기존 정보]</div>
					<div className={styles.row}>
						<div className={styles.label}>이름</div>
						<div className={styles.value}>{name}</div>
					</div>
					<div className={styles.row}>
						<div className={styles.label}>연락처</div>
						<div className={styles.value}>{phoneNumber}</div>
						<button className={styles.changeButton} onClick={handleVerificationNice}>
							변경
						</button>
					</div>
				</div>
				{isChanged && (
					<div className={styles.section}>
						<div className={styles.sectionTitle}>[변경 정보]</div>
						<div className={styles.row}>
							<div className={styles.label}>이름</div>
							<div className={styles.value}>{getValues('name')}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>연락처</div>
							<div className={styles.value}>{getValues('phoneNumber')}</div>
						</div>
					</div>
				)}
				{error && <div className={styles.error}>{error}</div>}
				<div className={styles.actions}>
					<button className={styles.cancelButton} onClick={handleCancel}>
						취소
					</button>
					<button
						className={styles.completeButton}
						onClick={handleComplete}
						disabled={!isChanged}>
						완료
					</button>
				</div>
			</div>
		</div>
	)
}
